const HERO_SEAT = 0;
const NUM_SEATS = 6;

/**
 * @typedef {'tight_passive'|'tight_aggressive'|'loose_passive'|'loose_aggressive'} ArchetypeKey
 * @typedef {object} AiPersonality
 * @property {number} tightness
 * @property {number} aggression
 * @property {number} bluffFrequency
 * @property {number} callStationFactor
 * @property {number} riskTolerance
 * @property {number} trapFrequency
 */

/** @type {Record<ArchetypeKey, AiPersonality>} */
export const ARCHETYPES = {
  tight_passive: {
    tightness: 0.85,
    aggression: 0.25,
    bluffFrequency: 0.1,
    callStationFactor: 0.2,
    riskTolerance: 0.3,
    trapFrequency: 0.2,
  },
  tight_aggressive: {
    tightness: 0.8,
    aggression: 0.75,
    bluffFrequency: 0.25,
    callStationFactor: 0.15,
    riskTolerance: 0.65,
    trapFrequency: 0.25,
  },
  loose_passive: {
    tightness: 0.3,
    aggression: 0.2,
    bluffFrequency: 0.1,
    callStationFactor: 0.75,
    riskTolerance: 0.45,
    trapFrequency: 0.1,
  },
  loose_aggressive: {
    tightness: 0.35,
    aggression: 0.85,
    bluffFrequency: 0.45,
    callStationFactor: 0.35,
    riskTolerance: 0.8,
    trapFrequency: 0.15,
  },
};

const ARCHETYPE_KEYS = /** @type {ArchetypeKey[]} */ ([
  'tight_passive',
  'tight_aggressive',
  'loose_passive',
  'loose_aggressive',
]);

/** Short Hebrew labels for TV */
export const ARCHETYPE_LABEL_HE = {
  tight_passive: 'צמוד·שקט',
  tight_aggressive: 'צמוד·אגרסיבי',
  loose_passive: 'רחב·שקט',
  loose_aggressive: 'רחב·אגרסיבי',
};

const BOT_DISPLAY_NAMES = [
  'דני',
  'מאיה',
  'יוסי',
  'נועה',
  'עומר',
  'שירה',
  'איתי',
  'רון',
  'תמר',
  'גל',
  'ליאור',
  'הילה',
  'בן',
  'קארין',
  'אלון',
];

/** @typedef {'easy'|'medium'|'hard'} AiDifficulty */

function shuffle(arr, rng) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Five bot seats: random unique names, random archetypes (4 types + one duplicate draw),
 * random difficulty for the table.
 * @param {import('./engine.js').TableState} state
 * @param {() => number} [rng]
 */
export function initRandomOpponents(state, rng = Math.random) {
  const difficulties = /** @type {AiDifficulty[]} */ (['easy', 'medium', 'hard']);
  state.aiDifficulty = difficulties[Math.floor(rng() * difficulties.length)];

  const names = shuffle(BOT_DISPLAY_NAMES, rng).slice(0, NUM_SEATS - 1);
  const archetypePool = shuffle(
    [...ARCHETYPE_KEYS, ARCHETYPE_KEYS[Math.floor(rng() * 4)]],
    rng
  );

  /** @type {Record<number, { displayName: string, archetype: ArchetypeKey, personality: AiPersonality }>} */
  const roster = {};
  let ni = 0;
  for (let seat = 0; seat < NUM_SEATS; seat += 1) {
    if (seat === HERO_SEAT) continue;
    const key = archetypePool[ni];
    roster[seat] = {
      displayName: names[ni] || `שחקן ${seat + 1}`,
      archetype: key,
      personality: { ...ARCHETYPES[key] },
    };
    ni += 1;
  }
  state.opponentRoster = roster;
}

/**
 * @param {import('./engine.js').TableState} state
 * @param {number} seat
 * @returns {{ displayName: string, archetype: ArchetypeKey, personality: AiPersonality } | null}
 */
export function getOpponentMeta(state, seat) {
  if (!state.opponentRoster || seat === HERO_SEAT) return null;
  return state.opponentRoster[seat] || null;
}
